/**
 * Channel assignment + publish tracking panel for a post.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function ChannelsPanel({ post, onChanged }) {
  const [allChannels, setAllChannels] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rateStatuses, setRateStatuses] = useState({}); // {channel_id: rateStatus}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/channels", { params: { activo: true } });
        if (!cancelled) setAllChannels(data || []);
      } catch {
        if (!cancelled) setAllChannels([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const targets = post.channel_targets || [];
  const channelById = useMemo(() => Object.fromEntries(allChannels.map((c) => [c.id, c])), [allChannels]);

  // Stable dependency: re-fetch only when the SET of channel ids changes,
  // not when unrelated fields inside each target object are updated.
  const targetIdsKey = targets.map((t) => t.channel_id).join(",");

  // Preload rate status for each assigned channel so the "límite alcanzado"
  // badge shows up before the user even tries to publish. Calls run in
  // parallel so a long target list doesn't waterfall N round-trips.
  useEffect(() => {
    let cancelled = false;
    const ids = targetIdsKey ? targetIdsKey.split(",") : [];
    if (ids.length === 0) {
      queueMicrotask(() => { if (!cancelled) setRateStatuses({}); });
      return () => { cancelled = true; };
    }
    Promise.all(
      ids.map((id) =>
        api.get(`/channels/${id}/rate-status`)
          .then((r) => [id, r.data])
          .catch(() => null),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      const out = {};
      for (const pair of pairs) {
        if (pair) out[pair[0]] = pair[1];
      }
      setRateStatuses(out);
    });
    return () => { cancelled = true; };
  }, [targetIdsKey, post.id]);

  const buildMessage = (channelId) => {
    // What the seller pastes into the channel. We append a tracked link with
    // both ?p=<post> and ?c=<channel> so per-channel attribution actually works.
    const sep = (post.enlace_producto || "").includes("?") ? "&" : "?";
    const url = `${post.enlace_producto || ""}${sep}p=${post.id}&c=${channelId}`;
    const body = [
      post.titulo,
      "",
      post.contenido,
      "",
      `🛒 ${url}`,
      (post.hashtags || []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" "),
    ].filter(Boolean).join("\n");
    return body;
  };

  const copyAndOpen = async (target) => {
    const ch = channelById[target.channel_id];
    if (!ch) {
      toast.error("Canal no encontrado");
      return;
    }
    try {
      await navigator.clipboard.writeText(buildMessage(target.channel_id));
      toast.success(`Mensaje copiado · abre ${ch.nombre} y pega`);
    } catch {
      toast.message("Copia manual del mensaje (tu navegador bloqueó el portapapeles)");
    }
    if (ch.link) {
      window.open(ch.link, "_blank", "noopener,noreferrer");
    }
  };

  const markPublished = async (target) => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/channels/${target.channel_id}/publish`, { notas: "" });
      toast.success("Marcado como publicado");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    } finally {
      setBusy(false);
    }
  };

  const skip = async (target) => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/channels/${target.channel_id}/skip`);
      toast.success("Canal saltado");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo saltar");
    } finally {
      setBusy(false);
    }
  };

  const removeTarget = async (target) => {
    if (!window.confirm("¿Quitar este canal del plan?")) return;
    setBusy(true);
    try {
      await api.delete(`/posts/${post.id}/channels/${target.channel_id}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border border-zinc-200" data-testid={`channels-panel-${post.id}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-200">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted2 font-semibold">
          📣 Distribución por canales · {targets.length}
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="text-[11px] text-klein hover:underline font-semibold"
          data-testid={`assign-channels-${post.id}`}
        >
          + Asignar canales
        </button>
      </div>
      {targets.length === 0 ? (
        <div className="px-3 py-3 text-[11px] text-muted2">
          Aún no asignas canales para distribuir esta publicación. Pulsa <strong>+ Asignar canales</strong> para elegir dónde la vas a compartir.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {targets.map((t) => {
            const ch = channelById[t.channel_id];
            const rate = rateStatuses[t.channel_id];
            const overLimit = rate?.over_limit;
            const statusStyle = {
              pending:   { bg: "#FFF8E1", color: "#7C5807", label: "Pendiente" },
              published: { bg: "#E8F5E9", color: "#1B5E20", label: "Publicado" },
              skipped:   { bg: "#F4F4F5", color: "#52525B", label: "Saltado" },
            }[t.status] || { bg: "#F4F4F5", color: "#52525B", label: t.status };
            return (
              <div key={t.channel_id} className="px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`target-${post.id}-${t.channel_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">
                      {ch?.nombre || `(canal ${t.channel_id.slice(0,8)})`}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5"
                      style={{ background: statusStyle.bg, color: statusStyle.color }}
                    >
                      {statusStyle.label}
                    </span>
                    {ch?.plataforma && (
                      <span className="text-[10px] text-zinc-500">{ch.plataforma}</span>
                    )}
                    {overLimit && (
                      <span className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 bg-[#FFEBEE] text-[#B71C1C]">
                        Límite semanal
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted2 mt-0.5">
                    {t.published_at && `Publicado: ${new Date(t.published_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })} · `}
                    {t.clicks > 0 && `${t.clicks} click${t.clicks === 1 ? "" : "s"} · `}
                    {rate && `Esta semana: ${rate.this_week}/${rate.cap}`}
                  </div>
                </div>
                {t.status === "pending" && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => copyAndOpen(t)}
                      disabled={busy || !ch}
                      className="text-[11px] px-2 py-1 bg-klein text-white hover:bg-klein-dark disabled:opacity-50"
                      data-testid={`copy-${post.id}-${t.channel_id}`}
                    >
                      📋 Copiar + abrir
                    </button>
                    <button
                      type="button"
                      onClick={() => markPublished(t)}
                      disabled={busy}
                      className="text-[11px] px-2 py-1 bg-[#1B5E20] text-white hover:bg-[#0F4513] disabled:opacity-50"
                      data-testid={`mark-${post.id}-${t.channel_id}`}
                    >
                      ✓ Marcar publicado
                    </button>
                    <button
                      type="button"
                      onClick={() => skip(t)}
                      disabled={busy}
                      className="text-[11px] px-2 py-1 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                      title="Saltar este canal"
                    >
                      ⏭
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeTarget(t)}
                  disabled={busy}
                  className="text-[11px] text-red-600 hover:underline"
                  title="Quitar del plan"
                  data-testid={`remove-${post.id}-${t.channel_id}`}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
      {pickerOpen && (
        <ChannelPicker
          post={post}
          channels={allChannels}
          currentTargets={targets}
          rateStatuses={rateStatuses}
          onClose={() => setPickerOpen(false)}
          onSaved={() => { setPickerOpen(false); onChanged?.(); }}
        />
      )}
    </div>
  );
}


function ChannelPicker({ post, channels, currentTargets, rateStatuses, onClose, onSaved }) {
  const initial = new Set((currentTargets || []).map((t) => t.channel_id));
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/channels`, { channel_ids: Array.from(selected) });
      toast.success("Canales asignados");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full max-h-[80vh] overflow-y-auto p-5 border border-zinc-200" onClick={(e) => e.stopPropagation()} data-testid="channel-picker">
        <h3 className="font-bold text-lg mb-2">Elige los canales</h3>
        <p className="text-xs text-zinc-600 mb-3">
          Selecciona dónde vas a compartir esta publicación. Las marcas en rojo ya alcanzaron su tope semanal.
        </p>
        {channels.length === 0 ? (
          <div className="text-center py-6 text-sm text-zinc-500">
            No tienes canales registrados aún. <a href="/canales" className="text-klein hover:underline">Agrega uno →</a>
          </div>
        ) : (
          <div className="space-y-1">
            {channels.map((c) => {
              const rate = rateStatuses[c.id];
              const over = rate?.over_limit;
              return (
                <label
                  key={c.id}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer border ${selected.has(c.id) ? "border-klein bg-klein/5" : "border-zinc-200 hover:bg-zinc-50"}`}
                  data-testid={`picker-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="accent-klein w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.nombre}</div>
                    <div className="text-[10px] text-zinc-500">
                      {c.plataforma}
                      {c.miembros_estimados > 0 && ` · ${c.miembros_estimados.toLocaleString()} miembros`}
                      {rate && ` · esta semana ${rate.this_week}/${rate.cap}`}
                    </div>
                  </div>
                  {over && (
                    <span className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 bg-[#FFEBEE] text-[#B71C1C]">
                      Tope
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50">Cancelar</button>
          <button type="button" onClick={save} disabled={busy} className="px-4 py-2 text-sm font-bold bg-klein text-white hover:bg-klein-dark disabled:opacity-50" data-testid="picker-save">
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lookup table for decision actions — keeps the UI consistent and translatable.
